import { ReactNode, useCallback, useRef, useState } from 'react';

import { Box, useTheme } from '@sbv-clients/ui-shared';

export const ResizablePanel = ({
  children,
  defaultHeight = 300,
  minHeight = 100,
  maxHeight,
}: {
  children: ReactNode;
  defaultHeight?: number;
  minHeight?: number;
  // Can't be dragged taller than this (e.g. the content's own height).
  maxHeight?: number;
}) => {
  const theme = useTheme();
  // Follows defaultHeight (e.g. once the content is measured) until dragged.
  const [draggedHeight, setHeight] = useState<number>();
  const height =
    draggedHeight === undefined
      ? defaultHeight
      : Math.min(maxHeight ?? Infinity, draggedHeight);
  const dragState = useRef({ isDragging: false, startY: 0, startHeight: 0 });

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = {
        isDragging: true,
        startY: e.clientY,
        startHeight: height,
      };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragState.current.isDragging) return;
        const delta = ev.clientY - dragState.current.startY;
        setHeight(
          Math.min(
            maxHeight ?? Infinity,
            Math.max(minHeight, dragState.current.startHeight + delta)
          )
        );
      };

      const onMouseUp = () => {
        dragState.current.isDragging = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [height, minHeight, maxHeight]
  );

  return (
    <Box>
      <Box
        sx={{
          height,
          // Clip, but leave room for the card shadow inside (it reaches ~4px).
          overflow: 'clip',
          overflowClipMargin: '6px',
        }}
      >
        {children}
      </Box>
      <Box
        onMouseDown={onMouseDown}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Drag to resize"
        sx={{
          height: '12px',
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          userSelect: 'none',
          '&::after': {
            content: '""',
            display: 'block',
            width: '36px',
            height: '4px',
            borderRadius: '2px',
            backgroundColor: theme.custom?.controlBorder,
            transition: 'background-color 120ms',
          },
          '&:hover::after': {
            backgroundColor: theme.palette.primary.main,
          },
        }}
      />
    </Box>
  );
};
